// ============================================
// src/components/maintenance/MaintenanceTaskDetail.js
// Mobile-Optimized Task Detail & Completion Modal
// ============================================

import React, { useState } from 'react';

function MaintenanceTaskDetail({ 
  task, 
  onUpdate, 
  onComplete, 
  onClose, 
  isAdmin 
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [completionData, setCompletionData] = useState({
    notes: '',
    cost: task.estimated_cost || '',
    images: [],
    issues_found: '',
    parts_replaced: '',
    quality_rating: 5,
    createExpense: true
  });
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    if (!completionData.notes) {
      alert('Please add completion notes');
      return;
    }

    setLoading(true);
    try {
      await onComplete(task, completionData);
      onClose();
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    await onUpdate(task.id, { status: newStatus });
    onClose();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return 'Not specified';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getRecurrenceText = (pattern) => {
    if (!pattern) return 'One-time task';
    
    const { type, interval, dayOfMonth, endDate } = pattern;
    let text = `Repeats every ${interval} ${type}`;
    
    if (type === 'monthly' && dayOfMonth) {
      text += ` on day ${dayOfMonth}`;
    }
    
    if (endDate) {
      text += ` until ${new Date(endDate).toLocaleDateString()}`;
    }
    
    return text;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '10px 10px 0 0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                {task.maintenance_categories?.icon} {task.title}
              </h3>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                {task.maintenance_categories?.name}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Status and Priority Badges */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginTop: '15px'
          }}>
            <span style={{
              padding: '5px 12px',
              borderRadius: '15px',
              background: 'rgba(255,255,255,0.2)',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {task.status?.replace('_', ' ').toUpperCase()}
            </span>
            <span style={{
              padding: '5px 12px',
              borderRadius: '15px',
              background: 'rgba(255,255,255,0.2)',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {task.priority?.toUpperCase()} PRIORITY
            </span>
            {task.task_type === 'recurring' && (
              <span style={{
                padding: '5px 12px',
                borderRadius: '15px',
                background: 'rgba(255,255,255,0.2)',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                🔄 RECURRING
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          background: '#f8f9fa'
        }}>
          <button
            onClick={() => setActiveTab('details')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'details' ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'details' ? '3px solid #667eea' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'details' ? '600' : '400',
              color: activeTab === 'details' ? '#667eea' : '#666'
            }}
          >
            Details
          </button>
          {isAdmin && task.status !== 'completed' && (
            <button
              onClick={() => setActiveTab('complete')}
              style={{
                flex: 1,
                padding: '15px',
                background: activeTab === 'complete' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'complete' ? '3px solid #667eea' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'complete' ? '600' : '400',
                color: activeTab === 'complete' ? '#667eea' : '#666'
              }}
            >
              Complete Task
            </button>
          )}
          <button
            onClick={() => setActiveTab('history')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'history' ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'history' ? '3px solid #667eea' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'history' ? '600' : '400',
              color: activeTab === 'history' ? '#667eea' : '#666'
            }}
          >
            History
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {activeTab === 'details' && (
            <div>
              {/* Task Details */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Task Information</h4>
                
                {task.description && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={labelStyle}>Description</label>
                    <p style={{ margin: 0, color: '#666', lineHeight: '1.5' }}>
                      {task.description}
                    </p>
                  </div>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '15px'
                }}>
                  <div>
                    <label style={labelStyle}>Scheduled Date</label>
                    <p style={valueStyle}>{formatDate(task.scheduled_date)}</p>
                  </div>
                  <div>
                    <label style={labelStyle}>Scheduled Time</label>
                    <p style={valueStyle}>{formatTime(task.scheduled_time)}</p>
                  </div>
                  <div>
                    <label style={labelStyle}>Duration</label>
                    <p style={valueStyle}>{task.duration_hours || 1} hours</p>
                  </div>
                  <div>
                    <label style={labelStyle}>Estimated Cost</label>
                    <p style={valueStyle}>₹{task.estimated_cost || 0}</p>
                  </div>
                </div>

                {task.task_type === 'recurring' && (
                  <div style={{ marginTop: '15px' }}>
                    <label style={labelStyle}>Recurrence</label>
                    <p style={valueStyle}>{getRecurrenceText(task.recurrence_pattern)}</p>
                  </div>
                )}
              </div>

              {/* Assignment Details */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Assignment</h4>
                
                {task.service_vendors ? (
                  <div style={{
                    background: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>Vendor</label>
                      <p style={{ ...valueStyle, fontSize: '16px', fontWeight: '600' }}>
                        {task.service_vendors.name}
                      </p>
                    </div>
                    <div>
                      <label style={labelStyle}>Contact</label>
                      <a
                        href={`tel:${task.service_vendors.phone}`}
                        style={{
                          display: 'inline-block',
                          padding: '8px 15px',
                          background: '#28a745',
                          color: 'white',
                          borderRadius: '5px',
                          textDecoration: 'none',
                          fontWeight: '600'
                        }}
                      >
                        📞 {task.service_vendors.phone}
                      </a>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#999' }}>No vendor assigned</p>
                )}
              </div>

              {/* AMC Details */}
              {task.is_amc && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>AMC Details</h4>
                  <div style={{
                    background: '#e3f2fd',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>AMC End Date</label>
                      <p style={valueStyle}>
                        {task.amc_end_date ? formatDate(task.amc_end_date) : 'Not specified'}
                      </p>
                    </div>
                    {task.amc_document_url && (
                      <div>
                        <label style={labelStyle}>AMC Document</label>
                        <a
                          href={task.amc_document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#667eea',
                            textDecoration: 'none'
                          }}
                        >
                          📄 View Document
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {task.notes && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Notes</h4>
                  <p style={{
                    margin: 0,
                    padding: '15px',
                    background: '#fff3cd',
                    borderRadius: '8px',
                    color: '#856404',
                    lineHeight: '1.5'
                  }}>
                    {task.notes}
                  </p>
                </div>
              )}

              {/* Quick Actions */}
              {isAdmin && task.status !== 'completed' && (
                <div style={{ marginTop: '30px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Quick Actions</h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '10px'
                  }}>
                    {task.status === 'scheduled' && (
                      <button
                        onClick={() => handleStatusUpdate('in_progress')}
                        style={actionButtonStyle('#17a2b8')}
                      >
                        ▶️ Start Task
                      </button>
                    )}
                    {task.status === 'in_progress' && (
                      <button
                        onClick={() => setActiveTab('complete')}
                        style={actionButtonStyle('#28a745')}
                      >
                        ✅ Mark Complete
                      </button>
                    )}
                    <button
                      onClick={() => handleStatusUpdate('cancelled')}
                      style={actionButtonStyle('#dc3545')}
                    >
                      ❌ Cancel Task
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'complete' && (
            <div>
              <h4 style={{ margin: '0 0 20px 0', color: '#333' }}>Complete Task</h4>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Completion Notes *</label>
                <textarea
                  value={completionData.notes}
                  onChange={(e) => setCompletionData({...completionData, notes: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    minHeight: '100px',
                    fontSize: '14px'
                  }}
                  placeholder="Describe the work done..."
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Actual Cost (₹)</label>
                <input
                  type="number"
                  value={completionData.cost}
                  onChange={(e) => setCompletionData({...completionData, cost: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter actual cost"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Issues Found</label>
                <textarea
                  value={completionData.issues_found}
                  onChange={(e) => setCompletionData({...completionData, issues_found: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    minHeight: '60px',
                    fontSize: '14px'
                  }}
                  placeholder="Any issues discovered during maintenance..."
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Parts Replaced</label>
                <input
                  type="text"
                  value={completionData.parts_replaced}
                  onChange={(e) => setCompletionData({...completionData, parts_replaced: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="List any parts that were replaced..."
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Quality Rating</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setCompletionData({...completionData, quality_rating: rating})}
                      style={{
                        padding: '10px',
                        background: completionData.quality_rating >= rating ? '#ffc107' : '#f0f0f0',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '20px'
                      }}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </div>

              {completionData.cost > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={completionData.createExpense}
                      onChange={(e) => setCompletionData({...completionData, createExpense: e.target.checked})}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={labelStyle}>Create expense entry for this task</span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleComplete}
                  disabled={loading || !completionData.notes}
                  style={{
                    flex: 1,
                    padding: '15px',
                    background: loading || !completionData.notes ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading || !completionData.notes ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  {loading ? 'Completing...' : 'Complete Task'}
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  style={{
                    flex: 1,
                    padding: '15px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h4 style={{ margin: '0 0 20px 0', color: '#333' }}>Task History</h4>
              <p style={{ color: '#666' }}>
                History tracking will show previous completions for recurring tasks.
              </p>
              {/* Add history list here when implementing */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  color: '#999',
  marginBottom: '5px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const valueStyle = {
  margin: 0,
  fontSize: '14px',
  color: '#333',
  fontWeight: '500'
};

const actionButtonStyle = (color) => ({
  padding: '12px',
  background: color,
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '14px',
  width: '100%'
});

export default MaintenanceTaskDetail;